// Real California marine species data for the Junior Crew Fish Guide.
// Sizes, ages and habitats come from CDFW / NOAA species profiles; the
// size and bag rules summarise California ocean sport fishing regulations,
// which change every year — always check the current CDFW rules.

export type CaSpeciesFacts = {
  /** Scientific (Latin) name. */
  sci: string;
  family: string;
  /** Documented maximum size. */
  maxSize: string;
  /** Documented maximum age. */
  maxAge: string;
  /** Where it really lives off California. */
  range: string;
  depth: string;
  /** Current California minimum size rule (sport). */
  minSize: string;
  /** Current California daily bag limit (sport). */
  bagLimit: string;
  season: string;
  /** One real, checkable biology note. */
  scienceNote: string;
};

export const CA_REG_SOURCE =
  "Rules summarised from California Department of Fish and Wildlife ocean sport fishing regulations. Limits change each year and by region — check the current CDFW rules with an adult before keeping a fish.";

export const CA_FACTS: Record<string, CaSpeciesFacts> = {
  "red-vermilion": {
    sci: "Sebastes miniatus",
    family: "Rockfishes (Sebastidae)",
    maxSize: "30 in · about 15 lb",
    maxAge: "Around 60 years",
    range: "Vancouver Island to central Baja; common off Southern California",
    depth: "50–500 ft over rocky reef",
    minSize: "No minimum size",
    bagLimit: "10 fish per day inside the 20-fish Rockfish-Cabezon-Greenling limit",
    season: "Open season and depth limits change by groundfish management area",
    scienceNote: "Rockfish do not lay eggs — the female gives birth to live larvae, sometimes over a million at once.",
  },
  "white-seabass": {
    sci: "Atractoscion nobilis",
    family: "Croakers (Sciaenidae)",
    maxSize: "5 ft · over 80 lb",
    maxAge: "Around 20 years",
    range: "Juneau, Alaska to Baja; kelp beds from Point Conception south",
    depth: "Surface to about 400 ft, usually in or near kelp",
    minSize: "28 inches total length",
    bagLimit: "3 fish per day — only 1 fish per day from March 15 to June 15",
    season: "Open all year, with the reduced spring limit",
    scienceNote: "It is not a bass at all — it is the largest croaker on the Pacific coast and drums with its swim bladder.",
  },
  flounder: {
    sci: "Platichthys stellatus",
    family: "Righteye flounders (Pleuronectidae)",
    maxSize: "3 ft · about 20 lb",
    maxAge: "Around 24 years",
    range: "Arctic Alaska to Santa Barbara; bays, estuaries and river mouths",
    depth: "Shallow bays down to about 900 ft",
    minSize: "No minimum size",
    bagLimit: "Counts toward the general 20-fish daily finfish limit",
    season: "Open all year",
    scienceNote: "Starry flounder hatch with one eye on each side, then one eye migrates across the skull as they turn flat.",
  },
  sheepshead: {
    sci: "Semicossyphus pulcher",
    family: "Wrasses (Labridae)",
    maxSize: "3 ft · about 36 lb",
    maxAge: "Over 50 years",
    range: "Monterey Bay to the Gulf of California; kelp and rocky reef",
    depth: "20–180 ft",
    minSize: "12 inches total length",
    bagLimit: "2 fish per day in California",
    season: "Open season varies by groundfish management area",
    scienceNote: "Every California sheephead starts life female and the largest fish change into males.",
  },
  bonito: {
    sci: "Sarda chiliensis",
    family: "Mackerels and tunas (Scombridae)",
    maxSize: "40 in · about 25 lb",
    maxAge: "Around 6 years",
    range: "Gulf of Alaska to Chile; common off Southern California in warm water",
    depth: "Surface to about 200 ft",
    minSize: "24 inches or 5 lb south of Point Conception",
    bagLimit: "10 fish per day, no more than 5 under the minimum size",
    season: "Open all year; best in warm-water months",
    scienceNote: "Bonito are warm-bodied tuna relatives and must keep swimming to push water over their gills.",
  },
  "spanish-mackerel": {
    sci: "Scomber japonicus",
    family: "Mackerels (Scombridae)",
    maxSize: "25 in · about 6 lb",
    maxAge: "Around 18 years",
    range: "Southeast Alaska to Baja; the most common pier fish in Southern California",
    depth: "Surface to about 1,000 ft, usually in big schools near the top",
    minSize: "No minimum size",
    bagLimit: "Counts toward the general 20-fish daily finfish limit",
    season: "Open all year",
    scienceNote: "Pacific chub mackerel is the real name for California's 'greenback' — true Spanish mackerel live in the Atlantic and Gulf.",
  },
  "red-snapper": {
    sci: "Sebastes spp. (sold as 'Pacific red snapper')",
    family: "Rockfishes (Sebastidae)",
    maxSize: "Depends on the rockfish — up to about 3 ft",
    maxAge: "Many rockfish live 50–100 years",
    range: "Rocky reefs all along the California coast",
    depth: "50–600 ft",
    minSize: "No minimum size for most rockfish",
    bagLimit: "10 fish per day inside the 20-fish Rockfish-Cabezon-Greenling limit",
    season: "Open season and depth limits change by groundfish management area",
    scienceNote: "No true snapper lives off California. Fish labelled 'red snapper' here are really rockfish — a completely different family.",
  },
  "bluefin-tuna": {
    sci: "Thunnus orientalis",
    family: "Mackerels and tunas (Scombridae)",
    maxSize: "10 ft · over 1,000 lb",
    maxAge: "Around 26 years",
    range: "Crosses the whole Pacific; spawns near Japan, feeds off California",
    depth: "Surface to over 1,500 ft",
    minSize: "No minimum size",
    bagLimit: "2 fish per day (federal Pacific bluefin limits change each year)",
    season: "Mostly summer and fall off Southern California",
    scienceNote: "Young Pacific bluefin swim more than 5,000 miles from Japan to California before they are two years old.",
  },
  croaker: {
    sci: "Umbrina roncador",
    family: "Croakers (Sciaenidae)",
    maxSize: "20 in · about 4 lb",
    maxAge: "Around 15 years",
    range: "Point Conception to the Gulf of California; surf and sandy bays",
    depth: "Surf zone to about 60 ft",
    minSize: "No minimum size",
    bagLimit: "10 croakers per day, of which no more than 2 may be California corbina",
    season: "Open all year",
    scienceNote: "Croakers make a drumming sound by vibrating muscles against the swim bladder — you can hear it when you land one.",
  },
  lingcod: {
    sci: "Ophiodon elongatus",
    family: "Greenlings (Hexagrammidae)",
    maxSize: "5 ft · about 80 lb",
    maxAge: "Around 25 years",
    range: "Alaska to Baja; rocky reef and pinnacles",
    depth: "30–1,000 ft",
    minSize: "22 inches total length",
    bagLimit: "2 fish per day in most of California",
    season: "Open season changes by groundfish management area",
    scienceNote: "About one in five lingcod has blue-green flesh from a harmless pigment — it cooks white and is safe to eat.",
  },
  dorado: {
    sci: "Coryphaena hippurus",
    family: "Dolphinfishes (Coryphaenidae)",
    maxSize: "6 ft · about 80 lb",
    maxAge: "About 4–5 years",
    range: "Warm water worldwide; reaches Southern California in warm summers",
    depth: "Top 100 ft, often under kelp paddies and floating debris",
    minSize: "No minimum size",
    bagLimit: "10 fish per day in California",
    season: "Warm-water months, usually late summer into fall",
    scienceNote: "Dorado are one of the fastest-growing fish on Earth — they can gain over 4 ft in a single year.",
  },
  halibut: {
    sci: "Paralichthys californicus",
    family: "Sand flounders (Paralichthyidae)",
    maxSize: "5 ft · about 70 lb",
    maxAge: "Around 30 years",
    range: "Quillayute River, Washington to southern Baja; sandy bottom and bays",
    depth: "Shallow surf to about 600 ft",
    minSize: "22 inches total length",
    bagLimit: "5 fish per day south of Point Sur, 3 fish per day north",
    season: "Open all year",
    scienceNote: "California halibut can be left-eyed or right-eyed — about half the population faces each way.",
  },
};
